import React, { useState } from 'react';
import { ChevronLeft, Eye, EyeOff, UsersRound } from 'lucide-react';
import { formatPhone } from '../../../../utils/masks';
import Button from '../../../../components/ui/buttons/Button';
import DashboardTab from './DashboardTab';
import ClientSwitchTabs from './ClientSwitchTabs';
import CustomActionButton from '../../../../components/ui/buttons/CustomActionButton';
import OverviewTab from './tabs/OverviewTab';
import PropertiesTab from './tabs/PropertiesTab';
import MovementsTab from './tabs/MovementsTab';
import DreamsTab from './tabs/DreamsTab';
import DocumentsTab from './tabs/DocumentsTab';
import InvestmentProfileTab from './tabs/InvestmentProfileTab';
import TaskTab from './tabs/TaskTab';
import InvestmentStrategyTab from './tabs/InvestmentStrategyTab';
import ProtectionsTab from './tabs/ProtectionsTab';

const PersonHeaderInfo = ({ person }) => (
  <div className="flex flex-col gap-1 min-w-0">
    <p className="font-semibold text-xl truncate" title={person?.name}>
      {person?.name}
    </p>

    <p
      className="text-base text-blueSteelDark truncate"
      title={`${person?.email} - ${formatPhone(person?.phone)}`}
    >
      {person?.email} - {formatPhone(person?.phone)}
    </p>

    <div className="flex items-center gap-3 flex-wrap">
      <div className="flex items-center gap-2">
        <span className="text-base font-semibold text-blueSteelDark">Suitability:</span>
        <div className="flex items-center gap-1.5 border border-bluePale rounded-full py-0.5 px-3 bg-tealSnow">
          <span
            className="w-2.5 h-2.5 rounded-full shrink-0"
            style={{ backgroundColor: person?.suitabilityProfile?.colorHex }}
          />
          <span className="text-sm text-blueSteelDark">{person?.suitabilityProfile?.text || 'Não informado'}</span>
        </div>
      </div>

      <p
        className="text-base text-blueSteelDark truncate"
        title={`Criado em ${person?.createdAt} - Planejador: ${person?.planner?.compactName} - Revisor: ${
          person?.reviewer?.compactName || 'Não cadastrado'
        }`}
      >
        Criado em {person?.createdAt} — <span className="font-semibold">Planejador: </span>
        {person?.planner?.compactName}
        <span> — </span>
        <span className="font-semibold">Revisor: </span>
        {person?.reviewer?.compactName || 'Não cadastrado'}
      </p>
    </div>
  </div>
);

const ClientDashboard = ({ client, sidebarItems }) => {
  const [activeTab, setActiveTab] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('tab') || 'overview';
  });

  const [isHeaderHidden, setIsHeaderHidden] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('hideHeader') === 'true';
  });

  const [scope, setScope] = useState('client');

  const canWrite = client.accessLevel !== 'read';

  const urlParams = new URLSearchParams(window.location.search);
  const from = urlParams.get('from') || 'commercial.my_clients';

  const handleTabSuccess = () => {
    if (activeTab === 'overview') {
      setActiveTab('assets_and_protections');
    } else if (activeTab === 'assets_and_protections') {
      setActiveTab('suitability');
    } else if (activeTab === 'suitability') {
      setActiveTab('personal');
    } else if (activeTab === 'personal') {
      setActiveTab('spouse');
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);

    const url = new URL(window.location);
    url.searchParams.set('tab', key);

    window.history.pushState({}, '', url);
  };

  const handleHeaderVisibilityToggle = () => {
    const nextValue = !isHeaderHidden;

    setIsHeaderHidden(nextValue);

    const url = new URL(window.location);

    if (nextValue) {
      url.searchParams.set('hideHeader', 'true');
    } else {
      url.searchParams.delete('hideHeader');
    }

    window.history.replaceState({}, '', url);
  };

  const tabs = [
    { key: 'overview', label: 'VISÃO GERAL' },
    { key: 'investment_profile', label: 'PERFIL FINANCEIRO' },
    { key: 'properties', label: 'PATRIMÔNIO' },
    { key: 'protections', label: 'PROTEÇÕES' },
    { key: 'dreams', label: 'SONHOS' },
    { key: 'investment_strategy', label: 'ESTRATÉGIA DE INVESTIMENTOS' },
    // { key: 'tasks', label: 'TAREFAS' },
    // { key: 'documents', label: 'DOCUMENTOS' },
    // { key: 'movements', label: 'MOVIMENTAÇÕES' },
  ];

  const selectedData = scope === 'spouse' ? client.spouse : client;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <h2 className="text-2xl font-semibold text-blueDarker flex items-center gap-2 font-jost">
          <UsersRound strokeWidth={1.25} className="w-6 h-6 text-tealMedium" />
          Meus Clientes
        </h2>

        <Button
          icon={ChevronLeft}
          onClick={() => {
            if (from.startsWith('client-profile')) {
              const parts = from.split('__');

              if (parts.length >= 2) {
                const tab = parts[1];
                window.location.href = `/app/planner/client/${client.id}/profile?tab=${tab}`;
              } else {
                window.location.href = sidebarItems.find((item) => item.featureKey === from)?.href || '/app/planner/my_clients';
              }
            } else {
              window.location.href = sidebarItems.find((item) => item.featureKey === from)?.href || '/app/planner/my_clients';
            }
          }}
        >
          VOLTAR
        </Button>
      </div>

      <div className="bg-white rounded-custom20 p-4 sm:p-6 lg:p-8 relative">
        <button
          type="button"
          onClick={handleHeaderVisibilityToggle}
          className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-blueLight text-blueDarker transition hover:opacity-80"
          aria-label={isHeaderHidden ? 'Mostrar informações do cabeçalho' : 'Ocultar informações do cabeçalho'}
          title={isHeaderHidden ? 'Mostrar informações' : 'Ocultar informações'}
        >
          {isHeaderHidden ? (
            <EyeOff className="h-5 w-5" strokeWidth={1.75} />
          ) : (
            <Eye className="h-5 w-5" strokeWidth={1.75} />
          )}
        </button>

        <div
          className="grid grid-cols-1 md:grid-cols-5 items-center gap-4 md:gap-6 pr-12"
          style={{
            filter: isHeaderHidden ? 'blur(7px)' : 'none',
            WebkitFilter: isHeaderHidden ? 'blur(7px)' : 'none',
            transition: 'filter 0.2s ease, -webkit-filter 0.2s ease',
            userSelect: isHeaderHidden ? 'none' : 'auto',
            pointerEvents: isHeaderHidden ? 'none' : 'auto',
          }}
        >
          <div className="flex flex-col gap-4 min-w-0 col-span-3">
            {scope === 'couple' ? (
              <>
                <PersonHeaderInfo person={client} />
                <PersonHeaderInfo person={client.spouse} />
              </>
            ) : (
              <PersonHeaderInfo person={selectedData} />
            )}
          </div>

          <div className="flex justify-start md:justify-center">
            <a
              href={`/app/planner/client/${client.id}/profile?from=client_dashboard_${activeTab}_${scope}`}
              className="shrink-0"
            >
              <CustomActionButton
                state="alt"
                onClick={() => console.log('consulta')}
                aria-label="Ir para consulta completa"
              >
                CONSULTA COMPLETA
              </CustomActionButton>
            </a>
          </div>

          <div className="flex justify-start md:justify-end min-w-0">
            {client.spouse ? (
              <ClientSwitchTabs
                options={[
                  { label: client.firstName, value: 'client' },
                  { label: client.spouse.firstName, value: 'spouse' },
                  { label: 'CASAL', value: 'couple' },
                ]}
                value={scope}
                onChange={setScope}
              />
            ) : (
              <p className="text-base text-blueSteelDark truncate">Não possui cônjuge vinculado</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-5">
        {tabs.map((tab) => (
          <DashboardTab
            key={tab.key}
            label={tab.label}
            active={activeTab === tab.key}
            completed={tab.completed}
            onClick={() => handleTabChange(tab.key)}
            style={'small'}
          />
        ))}
      </div>

      <div className="min-h-0 flex-1 mb-5">
        {activeTab === 'overview' && (
          <OverviewTab clientId={client.id} onSuccess={handleTabSuccess} scope={scope} canWrite={canWrite} />
        )}

        {activeTab === 'investment_profile' && (
          <InvestmentProfileTab clientId={client.id} onSuccess={handleTabSuccess} scope={scope} canWrite={canWrite} />
        )}

        {activeTab === 'properties' && (
          <PropertiesTab
            key={`properties-${scope}`}
            clientId={client.id}
            scope={scope}
            onSuccess={handleTabSuccess}
            canWrite={canWrite}
          />
        )}

        {activeTab === 'protections' && (
          <ProtectionsTab
            key={`protections-${scope}`}
            clientId={client.id}
            scope={scope}
            onSuccess={handleTabSuccess}
            canWrite={canWrite}
          />
        )}

        {activeTab === 'dreams' && (
          <DreamsTab
            key={`dreams-${scope}`}
            clientId={client.id}
            scope={scope}
            onSuccess={handleTabSuccess}
            canWrite={canWrite}
          />
        )}

        {activeTab === 'investment_strategy' && (
          <InvestmentStrategyTab clientId={client.id} onSuccess={handleTabSuccess} scope={scope} canWrite={canWrite} />
        )}

        {activeTab === 'tasks' && (
          <TaskTab
            key={`tasks-${scope}`}
            clientId={client.id}
            scope={scope}
            onSuccess={handleTabSuccess}
            canWrite={canWrite}
          />
        )}

        {activeTab === 'documents' && (
          <DocumentsTab
            key={`documents-${scope}`}
            clientId={client.id}
            scope={scope}
            onSuccess={handleTabSuccess}
            canWrite={canWrite}
          />
        )}

        {activeTab === 'movements' && (
          <MovementsTab key={`movements-${scope}`} clientId={client.id} scope={scope} onSuccess={handleTabSuccess} />
        )}
      </div>
    </div>
  );
};

export default ClientDashboard;
